import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { catchError, Observable, throwError } from 'rxjs';
import type {
  BdJobChainResponse,
  BdJobChainStatus,
  BdJobChainSubject,
  BdJobChainSummaryResponse,
} from './chain.model';

/**
 * The job system cannot answer: the backend sends 503 while its schema is not
 * installed, or the app config has no jobs endpoint. Callers fall back to the
 * status of the row that started the chain.
 */
export class BdJobSystemUnavailableError extends Error {
  constructor() {
    super('job system not available');
  }
}

export interface BdJobChainQuery {
  status?: BdJobChainStatus;
  /** 1..100, backend default 100. */
  limit?: number;
}

/** Query values of a `/jobs` list request; `undefined` leaves the parameter out. */
export function jobQueryParams(
  query: Readonly<Record<string, string | number | undefined>>,
): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) {
      params = params.set(key, value);
    }
  }
  return params;
}

/**
 * Base URL of `/jobs` (`<host>/api/jobs`), or `null` when the config lacks the
 * key — a local snapshot taken before the endpoint existed.
 */
export function jobsBaseUrl(environment: BdAppEnvironmentService): string | null {
  const base = environment.load<string | null>(
    ApiEndpointKey.BD_JOBS_RESOURCE_URL,
    true,
  );
  return base ? `${base.replace(/\/+$/, '')}/jobs` : null;
}

/** Maps the backend's 503 onto {@link BdJobSystemUnavailableError}. */
export function jobRequest<T>(
  baseUrl: string | null,
  request: (baseUrl: string) => Observable<T>,
): Observable<T> {
  if (!baseUrl) {
    return throwError(() => new BdJobSystemUnavailableError());
  }
  return request(baseUrl).pipe(
    catchError((error: unknown) =>
      throwError(() =>
        error instanceof HttpErrorResponse && error.status === 503
          ? new BdJobSystemUnavailableError()
          : error,
      ),
    ),
  );
}

/**
 * User surface of the job-status API: chains the user started or may UPDATE
 * a subject of. Read-only; no ETag, no push.
 */
@Injectable({ providedIn: 'root' })
export class BdJobChainApi {
  private readonly _http = inject(HttpClient);
  private readonly _baseUrl = jobsBaseUrl(inject(BdAppEnvironmentService));

  /** 404 when the chain does not exist or is not visible to the user. */
  public get(id: string): Observable<BdJobChainResponse> {
    return jobRequest(this._baseUrl, (base) =>
      this._http.get<BdJobChainResponse>(
        `${base}/chains/${encodeURIComponent(id)}`,
      ),
    );
  }

  /**
   * Chains of one resource, newest first. Meant for finding the chain of a
   * row (`limit: 1`); a page may be shorter than `limit`.
   */
  public findBySubject(
    subject: BdJobChainSubject,
    query: BdJobChainQuery = {},
  ): Observable<BdJobChainSummaryResponse[]> {
    return jobRequest(this._baseUrl, (base) =>
      this._http.get<BdJobChainSummaryResponse[]>(`${base}/chains`, {
        params: jobQueryParams({
          subjectType: subject.type,
          subjectId: subject.id,
          ...query,
        }),
      }),
    );
  }

  /**
   * The user's own chains plus those on resources they may UPDATE. A
   * dashboard query: load it on demand, do not poll it.
   */
  public findVisible(
    query: BdJobChainQuery = {},
  ): Observable<BdJobChainSummaryResponse[]> {
    return jobRequest(this._baseUrl, (base) =>
      this._http.get<BdJobChainSummaryResponse[]>(`${base}/chains`, {
        params: jobQueryParams({ ...query }),
      }),
    );
  }
}
